import { createFileRoute, useParams } from '@tanstack/react-router';
import { useEffect, useMemo } from 'react';
import { Spacer, VStack } from '@/components/Layout';
import { ViewportLoader } from '@/components/Loading';
import type { NavigationSectionGroup } from '@/components/Navigation/NavigationCard';
import { CForm } from '@/features/c-form/CForm';
import { ApiError } from '@/lib/api/error';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { NetworkAndSimSection } from './-components/sections/NetworkAndSimSection';
import { NetworkConfigurationSection } from './-components/sections/NetworkConfigurationSection';
import { NetworkSection } from './-components/sections/NetworkSection';
import { PoliciesAndProfilesSection } from './-components/sections/PoliciesAndProfilesSection';
import { RemoteManagementFeaturesSection } from './-components/sections/RemoteManagementFeaturesSection';
import { RemoteManagementHistorySection } from './-components/sections/RemoteManagementHistorySection';
import { SecurityConfigurationSection } from './-components/sections/SecurityConfigurationSection';
import { SecurityStatusSection } from './-components/sections/SecurityStatusSection';
import { useDevice, useUpdateDevice } from './-query';
import { mapGetDeviceToUpdateDevice } from './-query/selector';
import { EditDeviceControlFormStore, useEditDeviceControlFormStore } from './-store/store';
export const Route = createFileRoute('/_staff/devices/$id/_layout/control/')({
  component: DeviceControlTab
});

/**
 * デバイス Control タブ。
 *
 * 暫定実装: モノリシック GET/PATCH /v3/devices/{id} を使用。
 * 同一レイアウト内の他タブ（profile, status, lifecycle）は SDUI エンドポイントを使用しており、
 * 保存時に deviceDetailKey.all を無効化して整合性を保っている。
 * SDUI 化時に Section ベースの API に置き換わる想定。
 */
function DeviceControlTab() {
  const {
    id
  } = useParams({
    from: '/_staff/devices/$id/_layout'
  });
  const store = useMemo(() => EditDeviceControlFormStore.createStore(), []);
  return <EditDeviceControlFormStore.Provider value={store}>
      <DeviceControlContent id={id} />
    </EditDeviceControlFormStore.Provider>;
}
function DeviceControlContent({
  id
}: {
  id: string;
}) {
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'pages.devices.profile.navigation'
  });
  const {
    data,
    isPending: isQueryPending
  } = useDevice(id);
  const {
    values,
    actions
  } = useEditDeviceControlFormStore();
  const {
    mutateAsync: updateDeviceMutate,
    isPending: isMutationPending
  } = useUpdateDevice(id, {
    onError: error => {
      if (error instanceof ApiError) {
        actions.onApiError(error);
      }
    }
  });
  useEffect(() => actions.reset(mapGetDeviceToUpdateDevice(data)), [actions, data]);
  if (!data) {
    return <ViewportLoader />;
  }
  const navigationSectionGroups: NavigationSectionGroup[] = [{
    titleLabel: t('network'),
    to: '',
    sections: [{
      label: t('sectionTitle.network'),
      hash: 'network-section',
      icon: 'WaypointsIcon'
    }, {
      label: t('sectionTitle.networkAndSim'),
      hash: 'network-and-sim-section',
      icon: 'WaypointsIcon'
    }, {
      label: t('sectionTitle.networkConfiguration'),
      hash: 'network-configuration-section',
      icon: 'SettingsLucideIcon'
    }]
  }, {
    titleLabel: t('security'),
    to: '',
    sections: [{
      label: t('sectionTitle.securityStatus'),
      hash: 'security-status-section',
      icon: 'ShieldIcon'
    }, {
      label: t('sectionTitle.securityConfiguration'),
      hash: 'security-configuration-section',
      icon: 'SettingsLucideIcon'
    }, {
      label: t('sectionTitle.remoteManagementFeatures'),
      hash: 'remote-management-features-section',
      icon: 'RadioIcon'
    }, {
      label: t('sectionTitle.remoteManagementHistory'),
      hash: 'remote-management-history-section',
      icon: 'HistoryIcon'
    }, {
      label: t('sectionTitle.policiesAndProfiles'),
      hash: 'policies-and-profiles-section',
      icon: 'ShieldEllipsisIcon'
    }]
  }];
  const onSave = async () => {
    if (!actions.validate()) return false;
    return await updateDeviceMutate(values);
  };
  return <VStack className="h-full">
      <CForm editUnit="section" itemLayout="label" navigationSectionGroups={navigationSectionGroups} onSave={onSave} onReset={() => actions.reset(mapGetDeviceToUpdateDevice(data))}>
        <NetworkSection isPending={isMutationPending || isQueryPending} />
        <NetworkAndSimSection isPending={isMutationPending || isQueryPending} />
        <NetworkConfigurationSection isPending={isMutationPending || isQueryPending} />
        <Spacer size={16} />
        <SecurityStatusSection isPending={isMutationPending || isQueryPending} />
        <SecurityConfigurationSection isPending={isMutationPending || isQueryPending} />
        <RemoteManagementFeaturesSection isPending={isMutationPending || isQueryPending} />
        <RemoteManagementHistorySection isPending={isMutationPending || isQueryPending} />
        <PoliciesAndProfilesSection isPending={isMutationPending || isQueryPending} />
      </CForm>
    </VStack>;
}